import React, { useState, useEffect, useRef, useCallback } from 'react';
import _isEmpty from 'lodash/isEmpty';
import {
  Button,
  ButtonGroup,
  IconButton,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  TextField,
  Typography,
  useBreakpoint,
  InputAdornment
} from '@one-thd/sui-atomic-components';
import { TrashCan } from '@one-thd/sui-icons';
import { bool, func, string, oneOf } from 'prop-types';
import { Row, Col } from '@thd-olt-component-react/core-ui';
import { useConfigService } from '@thd-nucleus/experience-context';
import { useNavigate } from '@thd-olt-component-react/router';
import { useCabinetSearchParams } from '../../../../pages/cabinetVisualizer/hooks/useCabinetSearchParams';
import { useCabinetVisualizerStore, useCabinetVisualizerDigitalDataStore } from '../../../../store';
import {
  sendRemoveAnalytics,
  sendRemoveItemFromWallAnalytics,
} from '../../../../pages/cabinetVisualizer/analytics/analyticsFunctions';
import { selectDesignProducts, selectDesignMatrixSizeForDisplay } from '../../../../store/cabinetVisualizer/selectors';
import {
  getConstrainedWallSize
} from '../utils';
import { CABINET_VISUALIZER_DIGITAL_DATA } from '../../../../constants';

export const EditWallModal = ({
  open,
  onClose,
  designId,
  wallName,
  width: initialWidth,
  height: initialHeight,
  mode, // 'add' or 'edit'
  handleOpenEditWall
}) => {
  const internalWallSize = useRef({ Columns: 0, Rows: 0 });
  const navigate = useNavigate();
  const host = useConfigService('global-custom-commerce:host');
  const {
    cabinetVisualizer,
    deleteDesign,
    updateDesign,
    createAndUpdateDesign,
    updateCabinetVisualizer
  } = useCabinetVisualizerStore((state) => ({
    updateDesign: state.updateDesign,
    deleteDesign: state.deleteDesign,
    cabinetVisualizer: state.cabinetVisualizer,
    createAndUpdateDesign: state.createAndUpdateDesign,
    updateCabinetVisualizer: state.updateCabinetVisualizer
  }));

  const {
    trackEditWallInteractionEvent,
    trackEditWallOverlayEvent,
    trackAddWallOverlayEvent,
    trackAddWallInteractionEvent
  } = useCabinetVisualizerDigitalDataStore();
  const { updateDesignId } = useCabinetSearchParams();

  const { styleDesignIds: wallIds } = cabinetVisualizer;
  const matches = useBreakpoint('sm');
  const isDesktop = matches.greater;
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isInvalid, setIsInvalid] = useState(true);

  const regex = /^(\d+(\.\d*)?|\.\d+)$/;
  const decimalRegex = /^\d+\.\d{3,}$/;

  const [name, setName] = useState(wallName || '');
  const [width, setWidth] = useState(initialWidth || '');
  const [height, setHeight] = useState(initialHeight || '');
  const [widthError, setWidthError] = useState('');
  const [heightError, setHeightError] = useState('');

  useEffect(() => {
    setName(wallName || '');
    setWidth(initialWidth || '');
    setHeight(initialHeight || '');
    // Always reset errors on dimensions each time EditModal is open
    setWidthError('');
    setHeightError('');
  }, [wallName, initialWidth, initialHeight, open]);

  // Clear errors if both width and height are cleared by the user
  useEffect(() => {
    if (_isEmpty(width) && _isEmpty(height)) {
      setWidthError('');
      setHeightError('');
    }
  }, [width, height]);

  const handleName = (event) => setName(event.target.value);

  const handleWidth = (event) => {
    const value = event.target.value;
    if (_isEmpty(value) || regex.test(value)) {
      if (decimalRegex.test(value)) {
        setWidthError('This number can have a max of two decimal places.');
      } else if (Number(value) > 1000) {
        setWidthError('Maximum wall width is 1000');
      } else if (Number(value) < 24) {
        setWidthError('Minimum wall width is 24');
      } else {
        setWidthError('');
      }
      setWidth(value);
    }
  };

  const handleHeight = (event) => {
    const value = event.target.value;
    if (_isEmpty(value) || regex.test(value)) {
      if (decimalRegex.test(value)) {
        setHeightError('This number can have a max of two decimal places.');
      } else if (Number(value) > 1000) {
        setHeightError('Maximum wall height is 1000');
      } else if (Number(value) < 80) {
        setHeightError('Minimum wall height is 80');
      } else {
        setHeightError('');
      }
      setHeight(value);
    }
  };

  // Validation logic to determine if Save/Add button should be disabled
  const validate = () => {
    // Name is required
    if (_isEmpty(name.trim())) {
      return true;
    }

    // If width or height error exists, disable save/add
    if (!_isEmpty(widthError) || !_isEmpty(heightError)) {
      return true;
    }

    // Convert width and height to numbers for validation
    const widthNum = Number(width);
    const heightNum = Number(height);

    // If both width and height are exactly 0, allow (means unset)
    if (widthNum === 0 && heightNum === 0) {
      return false;
    }
    // If both width and height are greater than 0, allow
    if (widthNum > 0 && heightNum > 0) {
      return false;
    }

    if (widthNum === 0 || heightNum > 0) {
      return true;
    }
    if (heightNum === 0 || widthNum > 0) {
      return true;
    }

    const widthEntered = !_isEmpty(width) && !Number.isNaN(widthNum);
    const heightEntered = !_isEmpty(height) && !Number.isNaN(heightNum);

    // If only one dimension is entered, disable save/add
    if ((widthEntered && !heightEntered) || (!widthEntered && heightEntered)) {
      return true;
    }
    return false;
  };

  useEffect(() => {
    setIsInvalid(validate());
  }, [open, name, width, height, widthError, heightError]);

  const closeModal = () => {
    onClose();
  };

  useEffect(() => {
    if (open) {
      const isEdit = mode === 'edit';
      const eventName = isEdit
        ? CABINET_VISUALIZER_DIGITAL_DATA.CONFIGURATOR.EDIT_WALL
        : CABINET_VISUALIZER_DIGITAL_DATA.CONFIGURATOR.ADD_WALL;
      const trackEvent = isEdit ? trackEditWallOverlayEvent : trackAddWallOverlayEvent;
      trackEvent({ eventName });
    }
  }, [open]);

  useEffect(() => {
    internalWallSize.current = getConstrainedWallSize({
      Columns: width,
      Rows: height
    });
  }, [width, height, getConstrainedWallSize]);

  // Use useCallback to memoize the function and prevent unnecessary re-renders
  const getInitialWallSize = useCallback(() => {
    // Always use selectDesignMatrixSizeForDisplay for display values in the modal
    // This will show actual values without defaults when no values are set
    // But will show user-entered values when they exist
    return selectDesignMatrixSizeForDisplay(cabinetVisualizer, designId);
  }, [cabinetVisualizer, designId]);

  const addMeasurements = async () => {
    try {

      // Create update object with name and products
      const updateData = {
        DesignName: name,
      };

      // Always include Matrix object, even with empty values
      // This ensures previously entered values can be cleared
      updateData.Matrix = {
        // Use empty strings (not null or undefined) to explicitly clear values
        Rows: height,
        Columns: width,
      };

      await updateDesign(host, designId, updateData);
    } catch (err) {
      console.error('Error saving measurements:', err);
      // Handle errors appropriately (e.g., display an error message)
    }
    closeModal();
  };

  useEffect(() => {
    const initialWallSize = getInitialWallSize();
    setWidth(initialWallSize?.Columns || '');
    setHeight(initialWallSize?.Rows || '');
  }, [designId, getInitialWallSize]);

  const handleSaveOrAdd = async () => {
    if (mode === 'add') {
      // Build extraData with dimensions if both provided
      const extraData = width && height
        ? { Matrix: { Columns: Number(width), Rows: Number(height) } }
        : {};
      // Create the new design and await completion
      const newId = await createAndUpdateDesign(host, name, extraData, navigate);
      // Select the new wall in URL and store
      if (newId) {
        updateDesignId(newId);
        // Reorder walls so the new wall appears at the end
        const currentStyleIds = cabinetVisualizer.styleDesignIds || [];
        const reorderedStyleIds = [...currentStyleIds.filter((id) => id !== newId), newId];
        updateCabinetVisualizer({ selectedDesignId: newId, styleDesignIds: reorderedStyleIds });
      }
      trackAddWallInteractionEvent();

    } else {
      trackEditWallInteractionEvent();
      await addMeasurements();
    }
    // Close modal after state and URL updated
    closeModal();
  };

  const handleDeleteWallClick = () => {
    setIsDeleteConfirmOpen(true);
    onClose();
  };
  const handleDeleteWallCancel = () => {
    setIsDeleteConfirmOpen(false);
    handleOpenEditWall();
  };

  const getRemoveAllAnalyticsData = () => {
    const products = selectDesignProducts(cabinetVisualizer, designId);
    const items = [];
    products.forEach((product) => {
      const itemPrice = cabinetVisualizer.itemPrices.find((item) => item.itemId === product.Sku);
      // Skip products with no price how did they get here?
      if (!itemPrice || itemPrice.price == null) return;
      const updateExisting = items.findIndex((item) => item.productInfo.sku === String(product.Sku));
      if (updateExisting !== -1) {
        items[updateExisting].quantity += 1;
      } else {
        items.push({
          quantity: 1,
          productInfo: {
            sku: String(product.Sku)
          },
          price: {
            basePrice: String(itemPrice?.price)
          }
        });
      }
    });
    return items;
  };

  const handleDeleteWallConfirm = () => {
    // Always use the current wall's designId prop to delete, avoid stale URL params
    const designIdToDelete = designId;

    sendRemoveItemFromWallAnalytics('delete wall button');
    sendRemoveAnalytics(getRemoveAllAnalyticsData());
    deleteDesign(designIdToDelete, host, navigate);
    setIsDeleteConfirmOpen(false);
    closeModal();
  };

  return (
    <>
      <Modal size="sm" open={open} onClose={closeModal}>
        <ModalHeader onClose={closeModal}>
          {mode === 'add' ? 'Add a Wall' : 'Edit Wall'}
        </ModalHeader>
        <ModalBody>
          <Row className="sui-pl-0 sui-pr-0">
            <Col className="sui-pl-0 sui-pr-0" fallback="12">
              <TextField
                required
                label="Name "
                id="wall-name"
                fullWidth
                value={name}
                placeholder="e.g. Bathroom"
                helpMessage="Use a descriptive name to help you identify the location in your space."
                onChange={handleName}
              />
            </Col>
          </Row>
          <Row className="sui-pl-0 sui-pr-0 sui-mt-4 sui-w-full">
            <Col className="sui-pl-0 sui-pr-1" fallback="6">
              <TextField
                label={
                  (
                    <span>
                      Width{' '}
                      <span className="sui-text-xs sui-text-gray-500">
                        (optional)
                      </span>
                    </span>
                  )
                }
                id="wall-width"
                type="number"
                min="1"
                value={width}
                placeholder=""
                onChange={handleWidth}
                status={_isEmpty(widthError) ? '' : 'error'}
                statusMessage={widthError}
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">in</InputAdornment>
                  ),
                }}
              />
            </Col>
            <Col className="sui-pl-1 sui-pr-0" fallback="6">
              <TextField
                label={
                  (
                    <span>
                      Height{' '}
                      <span className="sui-text-xs sui-text-gray-500">
                        (optional)
                      </span>
                    </span>
                  )
                }
                id="wall-height"
                type="number"
                min="1"
                value={height}
                placeholder=""
                onChange={handleHeight}
                status={_isEmpty(heightError) ? '' : 'error'}
                statusMessage={heightError}
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">in</InputAdornment>
                  ),
                }}
              />
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter position="end">
          <div className={`sui-flex sui-items-center sui-justify-between sui-w-full ${isDesktop ? ''
            : 'sui-flex-col sui-gap-y-2'}`}
          >
            {/* Only show delete controls in edit mode and if more than one wall exists */}
            {mode === 'edit' && wallIds.length > 1 ? (
              <div className="sui-flex sui-items-center">
                <IconButton
                  className="sui-align-self-start"
                  icon={TrashCan}
                  iconSize="small"
                  onClick={handleDeleteWallClick}
                  aria-label="Delete Wall"
                />
                <Button
                  key="delete"
                  variant="text"
                  className="sui-flex sui-items-start text-red-600 sui-ml-2"
                  onClick={handleDeleteWallClick}
                  aria-label="Delete Wall"
                >
                  Delete Wall
                </Button>
              </div>
            ) : (
              // Render an empty div to preserve layout and spacing
              <div />
            )}
            <ButtonGroup>
              {[
                <Button
                  key="cancel"
                  variant="secondary"
                  onClick={closeModal}
                  style={isDesktop ? { order: 0 } : { order: 1 }}
                >
                  Cancel
                </Button>,
                <Button
                  key="save"
                  variant="primary"
                  onClick={handleSaveOrAdd}
                  disabled={isInvalid}
                >
                  {mode === 'add' ? 'Add Wall' : 'Save'}
                </Button>
              ]}
            </ButtonGroup>
          </div>
        </ModalFooter>
      </Modal>
      {mode === 'edit' && (
        <Modal open={isDeleteConfirmOpen} onClose={handleDeleteWallCancel}>
          <ModalHeader onClose={handleDeleteWallCancel}>Delete Wall</ModalHeader>
          <ModalBody>
            <Row className="sui-pl-0 sui-pr-0">
              <Col className="sui-pl-0 sui-pr-0" fallback="12">
                <Typography>
                  {'Are you sure you want to delete this wall? This change can\'t be undone.'}
                </Typography>
              </Col>
            </Row>
          </ModalBody>
          <ModalFooter position="end">
            <ButtonGroup>
              {[
                <Button
                  key="cancel"
                  variant="secondary"
                  onClick={handleDeleteWallCancel}
                  style={isDesktop ? { order: 0 } : { order: 1 }}
                >
                  Cancel
                </Button>,
                <Button key="delete" variant="primary" onClick={handleDeleteWallConfirm}>Delete Wall</Button>
              ]}
            </ButtonGroup>
          </ModalFooter>
        </Modal>
      )}
    </>
  );
};

EditWallModal.propTypes = {
  open: bool,
  onClose: func,
  designId: string,
  wallName: string.isRequired,
  width: string,
  height: string,
  mode: oneOf(['add', 'edit']),
  handleOpenEditWall: func // callback for open edit wall
};

EditWallModal.defaultProps = {
  designId: '',
  open: false,
  onClose: () => {},
  width: '',
  height: '',
  mode: 'edit',
  handleOpenEditWall: () => {}
};
